/**
 * Tracks the timestamp of the user's last meaningful interaction (mousedown,
 * keydown, touchstart) so the auth flow can refuse to silently refresh the
 * session for users who have been idle past a threshold. Without this, the
 * proactive refresh would keep an unattended session alive indefinitely.
 *
 * The timestamp is persisted in localStorage so it survives reloads and is
 * shared across tabs within the same origin. Writes are throttled to avoid
 * hammering storage on every keystroke.
 *
 * Note: this is frontend-only defense-in-depth. The backend refresh-token
 * cookie has its own lifetime and is the authoritative session control;
 * idle expiry should also be enforced server-side.
 */

const STORAGE_KEY = 'lastActivity';
const WRITE_THROTTLE_MS = 30_000;
export const INACTIVITY_LIMIT_MS = 12 * 60 * 60 * 1000; // 12 hours

let lastWrite = 0;

export function recordActivity() {
    const now = Date.now();
    if (now - lastWrite < WRITE_THROTTLE_MS) return;
    lastWrite = now;
    localStorage.setItem(STORAGE_KEY, String(now));
}

export function getLastActivity() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = parseInt(stored, 10);
    return Number.isFinite(parsed) ? parsed : null;
}

// Returns true only when we have a recorded activity timestamp AND it is older
// than the limit. A missing record returns false so a freshly-bootstrapped tab
// (e.g. the user just logged in on a different machine) is not treated as idle.
export function isInactive() {
    const last = getLastActivity();
    if (last === null) return false;
    return Date.now() - last > INACTIVITY_LIMIT_MS;
}

export function resetActivity() {
    lastWrite = Date.now();
    localStorage.setItem(STORAGE_KEY, String(lastWrite));
}

export function clearActivity() {
    lastWrite = 0;
    localStorage.removeItem(STORAGE_KEY);
}
